<template>
  <Card class="pricing-card" v-bind="$attrs" v-on="$listeners">
    <template #header>
      <FontAwesomeIcon v-if="meta.icon" :icon="['fad', meta.icon]" size="2x" />
      <div class="pricing-card__header">
        <div class="pricing-card__title">{{ name || meta.name }}</div>

        <div
          v-if="meta.description || meta.shortDescription"
          class="pricing-card__description"
          v-html="meta.shortDescription || meta.description"
        ></div>
        <div v-if="meta.condition" class="pricing-card__condition" v-html="meta.condition"></div>
        <slot name="header-bottom" />
      </div>
    </template>
    <template #default>
      <slot />
    </template>
    <template #footer>
      <Price :value="value" ref="price" :options="options" @subscription="subscription" />
      <div v-if="lockInPeriod && noticePeriod" class="pricing-card__condition">
        {{
          $t('orderConfirmationConditionShort', {
            lockInPeriod: lockInDate.fromNow(true),
            noticePeriod: noticeDate.fromNow(true),
          })
        }}
      </div>
      <Alert v-if="hasCustomSubscriptionPricing" class="mt-2">
        {{ $t('customPricingDisclaimer') }}
      </Alert>
      <div class="pricing-card__actions">
        <slot name="actions" />
      </div>
    </template>
  </Card>
</template>

<script>
import Card from '@/components/Card'
import { AlertComponent as Alert } from 'vue-elder-alert'

import Lines from './Lines'
import Price from './Price'
import moment from 'moment'

function parsePeriod(period) {
  return { value: period.substr(0, period.length - 1), unit: period.substr(-1) }
}

export default {
  props: {
    value: {
      type: [String, Array],
      required: true,
    },
    options: Object,
    name: String,
  },
  data() {
    return {
      lockInPeriod: null,
      lockInDate: null,
      noticePeriod: null,
      noticeDate: null,
    }
  },
  computed: {
    hasCustomSubscriptionPricing() {
      return this.$store.state.enums.CustomSubscriptionPricing.some(k => this.items.includes(k))
    },
    isArray() {
      return this.value instanceof Array
    },
    items() {
      return this.isArray ? this.value : [this.value]
    },
    meta() {
      return this.metas[this.items[0]] || {}
    },
    metas() {
      const meta = this.$store.state.enums.ProductMeta
      return Object.fromEntries(this.items.map(i => [i, meta[i]]))
    },
  },
  methods: {
    subscription(value) {
      this.lockInPeriod = parsePeriod(value.lockInPeriod)
      this.lockInDate = moment().add(this.lockInPeriod.value, this.lockInPeriod.unit)
      this.noticePeriod = parsePeriod(value.noticePeriod)
      this.noticeDate = moment().add(this.noticePeriod.value, this.noticePeriod.unit)
    },
  },
  components: {
    Card,
    Price,
    Alert,
    ...Lines,
  },
}
</script>

<style lang="scss">
.pricing-card {
  max-width: 600px;
  min-width: 320px;
  margin: 0 auto;

  &__label {
    margin-bottom: 0.25rem;
  }

  &__summary {
    &-title {
      font-size: 1.5em;
      font-weight: bold;
      margin: 0;
      line-height: 1;
    }

    .pricing-card-line:first-child {
      .pricing-card-line__item {
        margin-bottom: 0.5rem;
      }
    }

    .elder-button {
      font-size: 0.7em;
      margin-left: 1rem;
    }
  }

  &__details {
    border-top: 2px solid var(--border-color);
    padding-top: 1rem;
    margin-top: 1rem;
  }

  & > .card__header {
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 1.5rem;
    min-height: initial;
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__info {
    flex-grow: 1;
  }

  &__title {
    font-weight: bold;
    line-height: 1;
  }

  &__description {
    margin-top: 0.25rem;
  }

  &__condition {
    text-align: right;
    opacity: 0.7;
    font-size: 0.7em;
  }

  &__actions {
    margin-top: 1.5rem;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__lines {
    flex-grow: 1;

    &.elder-loader--active {
      justify-content: flex-start;
    }

    .elder-loader__element-content-inner {
      margin: 0;
      padding: 0;
      text-align: left;
      min-width: initial;
      display: flex;
      align-items: center;
    }

    .elder-loader__loading-message {
      margin: 0 0 0 0.5rem;
    }
  }

  &-line {
    text-align: right;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;

    &__value {
      font-size: 1.4em;
      display: inline-block;
      flex-shrink: 0;
      line-height: 1;
      min-width: 100px;
    }

    &__type {
      opacity: 0.75;
      font-size: 0.7em;
      font-weight: normal;
    }

    &__net {
      font-weight: bold;
    }

    &__gross {
      text-decoration: line-through;
      opacity: 0.5;
      margin-right: 0.5rem;
    }

    &__item {
      margin-right: auto;
      flex-grow: 1;
      text-align: left;

      & > *:empty {
        display: none;
      }
    }

    & + & {
      border-top: 1px dashed var(--border-color);
      margin-top: 5px;
      padding-top: 5px;
    }
  }

  .card__footer-content {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
